/**
 * Idiomas do produto (RNF-13): inglês é o padrão; pt-BR e es completos.
 * Os códigos são os mesmos da API (`en | pt-BR | es`).
 */
export const LANGS = ['en', 'pt-BR', 'es'] as const;
export type Lang = (typeof LANGS)[number];
export const DEFAULT_LANG: Lang = 'en';

/** Nome de cada idioma escrito no próprio idioma (para o seletor). */
export const LANG_NAMES: Record<Lang, string> = {
  en: 'English',
  'pt-BR': 'Português (Brasil)',
  es: 'Español',
};

/** Locale usado pelo Intl para datas e números. */
export function intlLocale(lang: Lang): string {
  return lang === 'en' ? 'en-US' : lang === 'es' ? 'es-ES' : 'pt-BR';
}

export function isLang(v: unknown): v is Lang {
  return typeof v === 'string' && (LANGS as readonly string[]).includes(v);
}

/** Converte um idioma do navegador (`pt`, `pt-PT`, `es-MX`, `en-GB`…) para um idioma do produto. */
export function fromBrowserTag(tag: string | undefined | null): Lang | null {
  if (!tag) return null;
  const t = tag.toLowerCase();
  if (t.startsWith('pt')) return 'pt-BR';
  if (t.startsWith('es')) return 'es';
  if (t.startsWith('en')) return 'en';
  return null;
}

/**
 * Ordem de escolha do idioma: o da pessoa (`/me.lang`) → o padrão do tenant →
 * o do navegador → inglês.
 */
export function resolveLang(o: { user?: string | null; tenant?: string | null; browser?: readonly string[] }): Lang {
  if (isLang(o.user)) return o.user;
  if (isLang(o.tenant)) return o.tenant;
  for (const b of o.browser ?? []) {
    const l = fromBrowserTag(b);
    if (l) return l;
  }
  return DEFAULT_LANG;
}

/** Texto localizado vindo da API (`{en, pt-BR, es}`); cai para inglês e depois para o primeiro valor. */
export type Localized = Partial<Record<Lang, string>> | Record<string, string>;
export function pickLocalized(v: unknown, lang: Lang): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const s = o[lang] ?? o.en ?? Object.values(o)[0];
    return typeof s === 'string' ? s : '';
  }
  return String(v);
}
